import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { ensureUnreachable } from '../../../common/src/util.ts';
import { registerCacheListener, unregisterCacheListener } from './client_cache_storage.ts';

export enum ContentKind {
  JSON = 'json',
  Text = 'text',
  Binary = 'binary',
}

// TODO(hewitt): rename to NetworkStatus
export enum NetworkResult {
  Loading = 'loading',
  Success = 'success',
  Failure = 'failure',
}

type UseFetchBaseContentType = string | object | ArrayBuffer;

export interface UseFetchResult<T> {
  content: T | undefined;
  result: NetworkResult;
  error: string | undefined;
}

// TODO(hewitt): https://www.npmjs.com/package/react-fetch-hook
// Watch this 15 minute video on AbortController: https://www.youtube.com/watch?v=2sdXSczmvNc
// TODO(hewitt): potentially replace with @uidotdev/usehooks useFetch when no longer experimental
// TODO(hewitt): also consider https://www.npmjs.com/package/use-http

// Important: URLs should be pre-encoded in order for listener callbacks to work properly
//            Even though fetch does escape spaces, there are other characters that it does not escape
// TODO(hewitt): The right answer is to use song slugs & hymnal/org ids instead of full names!
export function useFetch<T extends UseFetchBaseContentType>({
  url,
  contentKind,
}: {
  url: string | undefined;
  contentKind: ContentKind;
}): UseFetchResult<T> {
  const [content, setContent] = useState<T | undefined>();
  const [result, setResult] = useState(NetworkResult.Loading);
  const [error, setError] = useState<string | undefined>();

  // TODO(hewitt): fail immediately if offline
  // TODO(hewitt): retry when online
  // TODO(hewitt): retry after browser cache entry for this URL is finished refreshing

  useEffect(() => {
    // Clear these if the URL (or contentKind) changes, but not if the content subsequently fails to
    // load on cache invalidation. The synchronous resets below are what the eslint-react rule flags,
    // but they are required here: doing the reset inside the effect (rather than, say, during
    // render) is what keeps cache-invalidation re-fetches from wiping currently-visible content.
    // useEffect hooks are guaranteed to execute in code order.
    /* eslint-disable @eslint-react/set-state-in-effect */
    setContent(undefined);
    setResult(NetworkResult.Loading);
    setError(undefined);
    /* eslint-enable @eslint-react/set-state-in-effect */

    const controller = new AbortController();
    const signal = controller.signal;
    if (!url) {
      return;
    }
    const performFetch = async () => {
      try {
        const response = await fetch(url, { signal });
        if (response.ok) {
          setResult(NetworkResult.Success);
          switch (contentKind) {
            case ContentKind.JSON:
              setContent((await response.json()) as T);
              break;
            case ContentKind.Text:
              setContent((await response.text()) as T);
              break;
            case ContentKind.Binary:
              setContent((await response.arrayBuffer()) as T);
              break;
            default:
              ensureUnreachable(contentKind);
          }
        } else {
          setResult(NetworkResult.Failure);
          const message = `Network error fetching URL ${url}: ${response.statusText}`;
          setError(message);
          // expected transient - logged, not an error, so crash reports stay useful (issue #3517)
          console.log(message);
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          // fetch was canceled
        } else {
          const message = `Fetch error for URL ${url}: ${err}`;
          setError(message);
          // expected transient (offline, flaky connection) - see issue #3517
          console.log(message);
        }
      }
    };
    registerCacheListener(url, performFetch);
    void performFetch();
    return () => {
      controller.abort();
      unregisterCacheListener(url, performFetch);
    };
  }, [url, contentKind]);

  return { content, result, error };
}

export function useFetchArray<T extends UseFetchBaseContentType>(
  urls: string[],
  contentKind: ContentKind,
): { results: Array<UseFetchResult<T>>; fetchWorkers: ReactElement } {
  const [resultsByUrl, setResultsByUrl] = useState<Record<string, UseFetchResult<T>>>(() => ({}));

  const handleResult = useCallback((url: string, result: UseFetchResult<T>) => {
    setResultsByUrl((resultsByUrl) => ({ ...resultsByUrl, [url]: result }));
  }, []);

  const results = useMemo(
    () => urls.map((url) => resultsByUrl[url] ?? { result: NetworkResult.Loading }),
    [resultsByUrl, urls],
  );

  const fetchWorkers = useMemo(
    () => (
      <>
        {urls.map((url) => (
          <FetchWorker key={url} url={url} contentKind={contentKind} onResult={handleResult} />
        ))}
      </>
    ),
    [urls, contentKind, handleResult],
  );

  return { results, fetchWorkers };
}

function FetchWorker<T extends UseFetchBaseContentType>({
  url,
  contentKind,
  onResult,
}: {
  url: string;
  contentKind: ContentKind;
  onResult: (url: string, result: UseFetchResult<T>) => void;
}) {
  const { content, result, error } = useFetch<T>({ url, contentKind });

  useEffect(() => {
    onResult(url, { content, result, error });
  }, [content, result, error, onResult, url]);

  return null; // No DOM — this is a headless component
}
